import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import TurnstileWidget from './TurnstileWidget';
import ThankYouModal from './ThankYouModal';
import smsService from '../services/smsService';
import emailService from '../services/emailService';
import { leadsAPI } from '../services/api/leadsAPI';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUser, FiMail, FiPhone, FiCar, FiDollarSign, FiMessageSquare, FiSend, FiCheck } = FiIcons;

const LeadForm = ({ vehicleInfo = null, leadType = 'information', onClose }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    leadType: leadType,
    message: vehicleInfo 
      ? `I'm interested in the ${vehicleInfo.year} ${vehicleInfo.make} ${vehicleInfo.model}.` 
      : '',
    preferredContactMethod: 'email',
    preferredContactTime: '',
    budgetMin: '',
    budgetMax: '',
    financingNeeded: false,
    tradeInMake: '',
    tradeInModel: '',
    tradeInYear: '',
    tradeInMileage: '',
    tradeInCondition: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate turnstile token - TEMPORARILY DISABLED
    // if (!turnstileToken) {
    //   alert('Please complete the security verification');
    //   return;
    // }
    
    setIsSubmitting(true);

    try {
      const leadData = {
        ...formData,
        leadSource: 'website',
        vehicleMake: vehicleInfo?.make,
        vehicleModel: vehicleInfo?.model,
        vehicleYear: vehicleInfo?.year,
        interestedVehicleId: vehicleInfo?.id,
        budgetMin: formData.budgetMin ? parseFloat(formData.budgetMin) : null,
        budgetMax: formData.budgetMax ? parseFloat(formData.budgetMax) : null,
        tradeInYear: formData.tradeInYear ? parseInt(formData.tradeInYear) : null,
        tradeInMileage: formData.tradeInMileage ? parseInt(formData.tradeInMileage) : null
      };

      await leadsAPI.addLead(leadData);
      
      // Send email notification
      const emailResult = await emailService.sendLeadNotification(leadData);
      if (!emailResult.success) {
        console.warn('Lead email notification failed:', emailResult.error);
      }
      
      // Send auto-reply to customer
      await emailService.sendAutoReply(
        leadData.email, 
        `${leadData.firstName} ${leadData.lastName}`, 
        leadData.leadType.replace('_', ' ')
      );
      
      // Send SMS confirmation to customer
      if (formData.phone) {
        await smsService.sendLeadResponse(formData.phone, {
          year: vehicleInfo?.year || '',
          make: vehicleInfo?.make || '',
          model: vehicleInfo?.model || 'vehicle'
        });
      }
      
      setIsSubmitting(false);
      setSubmitted(true);
      setShowThankYou(true);

      // Fire Meta Pixel Lead event
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'Lead', { content_name: formData.leadType, content_category: 'lead_form' });
      }

      // Redirect to submission success page for Meta conversion tracking
      navigate(`/submission-success?type=${encodeURIComponent(formData.leadType)}`);

    } catch (error) {
      console.error('Error submitting lead:', error);
      alert('There was an error submitting your information. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleThankYouClose = () => {
    setShowThankYou(false);
    if (onClose) onClose();
  };

  if (submitted && !showThankYou) {
    return null;
  }

  return (
    <>
      {/* Thank You Modal with Google Ads Conversion Tracking */}
      <ThankYouModal
        isOpen={showThankYou}
        onClose={handleThankYouClose}
        title="Thank You!"
        message={`We'll contact you within 24 hours to discuss your ${formData.leadType.replace('_', ' ')} request.`}
        autoClose={true}
        autoCloseDelay={5000}
      />
      
      {submitted ? null : (
    <motion.form
      onSubmit={handleSubmit}
      className="space-y-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header */}
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          {leadType === 'test_drive' && 'Schedule a Test Drive'}
          {leadType === 'financing' && 'Get Financing Information'}
          {leadType === 'trade_in' && 'Trade-In Evaluation'}
          {leadType === 'information' && 'Request Information'}
        </h3>
        {vehicleInfo && (
          <p className="text-gray-600">
            {vehicleInfo.year} {vehicleInfo.make} {vehicleInfo.model} - ${vehicleInfo.price?.toLocaleString()}
          </p>
        )}
      </div>

      {/* Personal Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            First Name *
          </label>
          <div className="relative">
            <SafeIcon icon={FiUser} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="John"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Last Name *
          </label>
          <input
            type="text"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Doe"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email Address *
          </label>
          <div className="relative">
            <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="john@example.com"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Phone Number *
          </label>
          <div className="relative">
            <SafeIcon icon={FiPhone} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="(817) 382-2316"
            />
          </div>
        </div>
      </div>

      {/* Contact Preferences */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Preferred Contact Method
          </label>
          <select
            name="preferredContactMethod"
            value={formData.preferredContactMethod}
            onChange={handleChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="email">Email</option>
            <option value="phone">Phone Call</option>
            <option value="text">Text Message</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Best Time to Contact
          </label>
          <input
            type="text"
            name="preferredContactTime"
            value={formData.preferredContactTime}
            onChange={handleChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="e.g., Weekdays 9AM-5PM"
          />
        </div>
      </div>

      {/* Budget Information (for financing/purchase inquiries) */}
      {(leadType === 'financing' || leadType === 'information') && (
        <div>
          <h4 className="text-lg font-medium text-gray-900 mb-3">Budget Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Minimum Budget
              </label>
              <div className="relative">
                <SafeIcon icon={FiDollarSign} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="number"
                  name="budgetMin"
                  value={formData.budgetMin}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="25000"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Maximum Budget
              </label>
              <div className="relative">
                <SafeIcon icon={FiDollarSign} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="number"
                  name="budgetMax"
                  value={formData.budgetMax}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="35000"
                />
              </div>
            </div>
          </div>

          <div className="mt-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="financingNeeded"
                checked={formData.financingNeeded}
                onChange={handleChange}
                className="mr-2 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">I need financing assistance</span>
            </label>
          </div>
        </div>
      )}

      {/* Trade-in Information */}
      {leadType === 'trade_in' && (
        <div>
          <h4 className="text-lg font-medium text-gray-900 mb-3">Trade-in Vehicle Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Make</label>
              <input
                type="text"
                name="tradeInMake"
                value={formData.tradeInMake}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Toyota"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
              <input
                type="text"
                name="tradeInModel"
                value={formData.tradeInModel}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Camry"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
              <input
                type="number"
                name="tradeInYear"
                value={formData.tradeInYear}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="2020"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Mileage</label>
              <input
                type="number"
                name="tradeInMileage"
                value={formData.tradeInMileage}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="50000"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Condition</label>
              <select
                name="tradeInCondition"
                value={formData.tradeInCondition}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select condition</option>
                <option value="excellent">Excellent</option>
                <option value="good">Good</option>
                <option value="fair">Fair</option>
                <option value="poor">Poor</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Message */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Additional Comments
        </label>
        <div className="relative">
          <SafeIcon icon={FiMessageSquare} className="absolute left-3 top-4 text-gray-400" />
          <textarea
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={4}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Tell us more about your needs..."
          />
        </div>
      </div>

      {/* Turnstile Captcha */}
      {/* CAPTCHA Temporarily Disabled - Enable after adding domain to Cloudflare */}
      {/* <div className="flex justify-center my-6 pb-4">
        <div className="relative z-[100]">
          <TurnstileWidget
            ref={turnstileRef}
            onVerify={(token) => setTurnstileToken(token)}
            onError={() => setTurnstileToken(null)}
            onExpire={() => setTurnstileToken(null)}
          />
        </div>
      </div> */}

      {/* Submit Button */}
      <motion.button
        type="submit"
        disabled={isSubmitting}
        className={`w-full py-4 px-6 rounded-lg font-semibold text-white transition-all duration-300 flex items-center justify-center gap-2 ${
          isSubmitting 
            ? 'bg-gray-400 cursor-not-allowed' 
            : 'bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 transform hover:scale-[1.02] hover:shadow-lg'
        }`}
        whileHover={!isSubmitting ? { scale: 1.02 } : {}}
        whileTap={!isSubmitting ? { scale: 0.98 } : {}}
      >
        {isSubmitting ? (
          <>
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
            Submitting...
          </>
        ) : (
          <>
            <SafeIcon icon={FiSend} />
            Submit {leadType.replace('_', ' ').charAt(0).toUpperCase() + leadType.replace('_', ' ').slice(1)} Request
          </>
        )}
      </motion.button>

      <p className="text-xs text-gray-500 text-center mt-4">
        * Required fields. We respect your privacy and will never share your information.
      </p>
    </motion.form>
      )}
    </>
  );
};

export default LeadForm;
